import { useEffect, useRef } from 'react';

const MAZE_API_KEY = import.meta.env.VITE_MAZE_API_KEY;
const MAZE_LOADER_URL = 'https://snippet.maze.co/maze-universal-loader.js';
const MAZE_SESSION_KEY = 'maze-us';

declare global {
  interface Window {
    mazeUniversalSnippetApiKey?: string;
  }
}

/**
 * Injects the Maze UX research snippet into <head> on mount
 * and removes it on unmount to prevent duplication during SPA navigation.
 */
export const useMazeScript = (): void => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    if (!MAZE_API_KEY) {
      console.warn(
        'Maze API key not found in environment variables. Maze snippet will not be injected.'
      );
      return;
    }

    if (scriptRef.current) return;

    let sessionId: string | number;
    try {
      const stored = window.sessionStorage.getItem(MAZE_SESSION_KEY);
      sessionId = stored ?? new Date().getTime();
      if (!stored) {
        window.sessionStorage.setItem(MAZE_SESSION_KEY, String(sessionId));
      }
    } catch {
      sessionId = new Date().getTime();
    }

    const script = document.createElement('script');
    script.src = `${MAZE_LOADER_URL}?apiKey=${MAZE_API_KEY}`;
    script.async = true;

    scriptRef.current = script;
    window.mazeUniversalSnippetApiKey = MAZE_API_KEY;
    document.head.appendChild(script);

    return () => {
      if (scriptRef.current) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
      delete window.mazeUniversalSnippetApiKey;
    };
  }, []);
};
